import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState, useMemo } from 'react';
import { IconAlertTriangle, IconArrowRight, IconCheck, IconTrash, IconX, IconSearch, IconBox } from '@tabler/icons-react';
import LoadingIndicator from '../LoadingIndicator';
import { useMasterData } from '../../context/MasterDataContext';
import { toast } from 'react-toastify';

export default function ReviewModal({
  isOpen,
  onClose,
  operation,
  items,
  cabangList,
  onConfirm,
  loading
}) {
  const [formData, setFormData] = useState({
    cabang_id: '',
    keterangan: '',
    nomor_rak: ''
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [isOpenBakiModal, setIsOpenBakiModal] = useState(false);
  const [searchBaki, setSearchBaki] = useState('');

  const { bakiList = [] } = useMasterData() || {};
  const bakiOption = bakiList && bakiList.length > 0 ? bakiList : Array.from({ length: 20 }, (_, i) => i + 1);

  const filteredBakiOption = useMemo(() => {
    if (!searchBaki.trim()) return bakiOption;
    const q = searchBaki.toLowerCase().trim();
    return bakiOption.filter((opt) => {
      const str = String(opt).toLowerCase();
      return str.includes(q) || `baki ${str}`.includes(q);
    });
  }, [bakiOption, searchBaki]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({ cabang_id: '', keterangan: '', nomor_rak: '' });
      setShowConfirm(false);
      setIsOpenBakiModal(false);
      setSearchBaki('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (operation === 'hapus' && !formData.keterangan.trim()) {
      toast.warn('Keterangan wajib diisi untuk penghapusan');
      return;
    }
    if ((operation === 'transfer' || operation === 'pindah') && !formData.cabang_id) {
      toast.warn('Pilih cabang tujuan');
      return;
    }
    if (operation === 'pindah_baki' && !formData.nomor_rak) {
      toast.warn('Pilih nomor baki tujuan');
      return;
    }
    setShowConfirm(true);
  };

  const handleSubmit = () => {
    onConfirm(formData);
  };

  const getOperationTitle = () => {
    switch (operation) {
      case 'transfer': return 'Transfer ke Cabang Lain';
      case 'pindah': return 'Pindah Etalase / Brankas';
      case 'pindah_baki': return 'Pindah ke BAKI Lain';
      case 'hapus': return 'Hapus Barang';
      default: return 'Operasi Barang';
    }
  };

  const getOperationIcon = () => {
    switch (operation) {
      case 'transfer': return <IconArrowRight className="w-6 h-6" />;
      case 'pindah': return <IconArrowRight className="w-6 h-6" />;
      case 'pindah_baki': return <IconCheck className="w-6 h-6" />;
      case 'hapus': return <IconTrash className="w-6 h-6" />;
      default: return <IconCheck className="w-6 h-6" />;
    }
  };

  const getOperationColor = () => {
    switch (operation) {
      case 'transfer': return 'amber';
      case 'pindah': return 'blue';
      case 'pindah_baki': return 'emerald';
      case 'hapus': return 'red';
      default: return 'slate';
    }
  };

  const color = getOperationColor();

  function fake() {}

  return (
    <>
      {loading && <LoadingIndicator />}
      <Transition appear show={isOpen && !showConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-[99999]" onClose={fake}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex items-center justify-end p-4 min-h-screen">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden bg-white dark:bg-slate-900 sm:rounded-xl p-0 text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh] border dark:border-slate-800">
                  {/* Header */}
                  <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 flex items-center justify-between sticky top-0 z-10 w-full">
                    <div>
                      <Dialog.Title as="h2" className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100">
                        {getOperationTitle()}
                      </Dialog.Title>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                        {items.length === 1 ? '1 barang akan diproses' : `${items.length} barang akan diproses`}
                      </p>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 dark:hover:text-slate-300 transition-colors"
                    >
                      <IconX className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-5 overflow-y-auto flex-1 bg-slate-50/50 dark:bg-slate-900/50">
                    {/* Items Table */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/60 rounded-lg overflow-hidden shadow-sm mb-4">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-slate-600 dark:text-slate-300">
                          <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700/60">
                            <tr>
                              <th scope="col" className="px-3 py-2 font-semibold w-12 text-center">No</th>
                              <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 align-middle">Kode</th>
                              <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 align-middle">Barang</th>
                              <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 text-right align-middle">Berat</th>
                              <th scope="col" className="px-3 py-2 font-semibold border-l border-slate-200 dark:border-slate-700/60 align-middle">Cabang</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {items.map((item, i) => (
                              <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors bg-white dark:bg-slate-900">
                                <td className="px-3 py-2 text-center text-slate-400 dark:text-slate-500">{i + 1}</td>
                                <td className="px-3 py-2 font-medium border-l border-slate-200 dark:border-slate-700/60 align-middle text-slate-700 dark:text-slate-300">{item.kode_item}</td>
                                <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-slate-600 dark:text-slate-400 truncate max-w-[150px]">{item.jenis_kategori?.nama_jenis}</td>
                                <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-right tabular-nums text-slate-700 dark:text-slate-300">{item.berat} gr</td>
                                <td className="px-3 py-2 border-l border-slate-200 dark:border-slate-700/60 align-middle text-slate-600 dark:text-slate-400">{item.cabang?.nama_cabang}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Additional Form Fields */}
                    {(operation === 'transfer' || operation === 'pindah') && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                          Cabang Tujuan
                        </label>
                        <select
                          value={formData.cabang_id}
                          onChange={(e) => setFormData({ ...formData, cabang_id: e.target.value })}
                          className="w-full h-10 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 dark:focus:border-amber-500 cursor-pointer appearance-none shadow-sm dark:text-slate-200"
                        >
                          <option value="">Pilih Cabang Tujuan...</option>
                          {cabangList?.map((cb) => (
                            <option value={cb.id} key={cb.id}>
                              {cb.nama_cabang}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {operation === 'pindah_baki' && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                          Nomor BAKI Tujuan
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsOpenBakiModal(true)}
                          className="w-full h-10 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 dark:focus:border-emerald-500 cursor-pointer shadow-sm dark:text-slate-200 flex items-center justify-between text-left"
                        >
                          <span className={formData.nomor_rak ? "font-bold text-slate-800 dark:text-slate-100" : "text-slate-400 dark:text-slate-400"}>
                            {formData.nomor_rak ? `Baki ${formData.nomor_rak}` : 'Pilih Baki Tujuan...'}
                          </span>
                          <IconSearch size={16} className="text-slate-400 shrink-0" />
                        </button>
                      </div>
                    )}

                    {operation === 'hapus' && (
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                          Alasan Penghapusan (Keterangan)
                        </label>
                        <textarea
                          rows="3"
                          value={formData.keterangan}
                          onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                          className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-4 focus:ring-red-500/10 focus:border-red-500 transition-all resize-none text-sm outline-none shadow-sm"
                          placeholder="Contoh: Barang rusak, salah input, atau stok habis..."
                        ></textarea>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-500 italic flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                          Keterangan ini wajib diisi untuk dokumentasi sistem.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 flex flex-col-reverse sm:flex-row justify-end gap-3 sticky bottom-0 w-full z-10">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={onClose}
                      className="w-full sm:w-auto h-11 sm:h-10 px-6 inline-flex items-center justify-center gap-2 rounded-lg font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-300 transition-colors disabled:opacity-50"
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleConfirm}
                      className={`w-full sm:w-auto h-11 sm:h-10 px-8 inline-flex items-center justify-center gap-2 rounded-lg font-bold text-white bg-gradient-to-r from-${color}-500 to-${color}-600 hover:from-${color}-400 hover:to-${color}-500 active:from-${color}-600 active:to-${color}-700 shadow-md shadow-${color}-500/20 transition-all disabled:from-slate-300 disabled:to-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:cursor-not-allowed`}
                    >
                      {getOperationIcon()}
                      <span>Lanjut Proses</span>
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Confirmation Dialog */}
      <Transition appear show={showConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-[100000]" onClose={() => !loading && setShowConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-end p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-xl bg-white dark:bg-slate-900 p-6 text-left align-middle shadow-xl transition-all border dark:border-slate-800">
                  <div className="flex items-center justify-center mb-4 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-500/20 mx-auto">
                    <IconAlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-slate-900 dark:text-slate-100 text-center"
                  >
                    Konfirmasi {getOperationTitle()}
                  </Dialog.Title>
                  <div className="mt-2 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Anda yakin akan {operation === 'hapus' ? 'menghapus' : 'memproses'} <span className="font-bold text-slate-700 dark:text-slate-300">{items.length}</span> barang ini?
                    </p>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      disabled={loading}
                      className="w-full inline-flex justify-center rounded-xl border border-transparent bg-slate-100 dark:bg-slate-800 px-4 py-2.5 text-sm font-medium text-slate-900 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 disabled:opacity-50 transition-colors"
                      onClick={() => setShowConfirm(false)}
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      className="w-full inline-flex justify-center rounded-xl border border-transparent bg-amber-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 shadow-md shadow-amber-500/20 disabled:opacity-50 transition-all"
                      onClick={handleSubmit}
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                          <span>Proses...</span>
                        </div>
                      ) : (
                        <span>Ya, Proses</span>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal Pilih Baki Tujuan */}
      <Transition appear show={isOpenBakiModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100001]"
          onClose={() => {
            setIsOpenBakiModal(false);
            setSearchBaki('');
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-end p-4">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col max-h-[80vh] text-left align-middle transition-all">
                  {/* Header */}
                  <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-xs">
                        <IconBox size={18} />
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="font-bold text-sm text-slate-800 dark:text-slate-100">
                          Pilih Baki Tujuan
                        </Dialog.Title>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">Pilih baki tujuan pemindahan barang</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsOpenBakiModal(false);
                        setSearchBaki('');
                      }}
                      className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                    >
                      <IconX size={18} />
                    </button>
                  </div>

                  {/* Search Input */}
                  <div className="p-4 border-b border-slate-100 dark:border-slate-700">
                    <div className="relative">
                      <IconSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Cari nomor baki (misal: 1, 5, 12)..."
                        value={searchBaki}
                        onChange={(e) => setSearchBaki(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Options List */}
                  <div className="overflow-y-auto p-3 max-h-[50vh]">
                    {filteredBakiOption.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {filteredBakiOption.map((opt) => {
                          const isSelected = String(formData.nomor_rak) === String(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, nomor_rak: opt });
                                setIsOpenBakiModal(false);
                                setSearchBaki('');
                              }}
                              className={`p-3 rounded-xl border text-left transition-all flex items-center justify-between cursor-pointer ${
                                isSelected
                                  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-500 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300 font-bold shadow-sm'
                                  : 'bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-slate-700/50 font-medium'
                              }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <div
                                  className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs ${
                                    isSelected
                                      ? 'bg-emerald-500 text-white shadow-sm'
                                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                  }`}
                                >
                                  {opt}
                                </div>
                                <span className="text-xs">Baki {opt}</span>
                              </div>
                              {isSelected && (
                                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-sm text-slate-500 dark:text-slate-400">
                        Nomor baki tidak ditemukan
                      </div>
                    )}
                  </div>

                  {/* Selected Indicator Footer */}
                  {formData.nomor_rak && (
                    <div className="p-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center text-xs">
                      <span className="text-slate-500 dark:text-slate-400">
                        Terpilih: <strong className="text-emerald-600 dark:text-emerald-400 font-bold">Baki {formData.nomor_rak}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, nomor_rak: '' });
                          setSearchBaki('');
                        }}
                        className="text-rose-500 hover:text-rose-700 font-semibold cursor-pointer"
                      >
                        Reset Pilihan
                      </button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
